import { NavLink, Outlet } from 'react-router-dom';
import { Wallet, Crown, Receipt } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Billing section — in-page tab bar over three product surfaces:
//
//   /billing/credits   → metered AI credit wallet (top-ups, usage)
//   /billing/plans     → subscription plan tier (current + catalog)
//   /billing/invoices  → both kinds of invoice records
//
// Everything is scoped to the ACTIVE workspace (X-Workspace-Id).
// ─────────────────────────────────────────────────────────────────

const TABS = [
  { to: '/billing/credits', label: 'Credits', icon: Wallet },
  { to: '/billing/plans', label: 'Plan', icon: Crown },
  { to: '/billing/invoices', label: 'Invoices', icon: Receipt },
] as const;

export function BillingPage() {
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="shrink-0 px-8 pt-8 pb-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="bg-accent h-1.5 w-1.5 rounded-full" />
              Billing & workspace
            </p>
            <h1 className="text-ink text-[22px] font-bold tracking-tight">Billing</h1>
            <p className="text-ink-soft mt-1 text-[13px]">
              AI credits, your plan and invoice records.
            </p>
          </div>
        </div>

        {/* ── Tab bar ──────────────────────────────────────── */}
        <nav className="border-line bg-bg/60 mt-5 flex max-w-full items-center gap-1 overflow-x-auto rounded-full border p-1">
          {TABS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[13px] font-semibold whitespace-nowrap transition-all duration-150 ${
                  isActive ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
                }`
              }
            >
              <Icon size={13} strokeWidth={2.2} />
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* ── Active tab content ─────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        <Outlet />
      </div>
    </div>
  );
}
